import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
} from '@mui/material';
import { ViewModule, CalendarMonth, Delete, Assignment } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { TranslucentContainer } from '../../../../../components';
import RetailSpaceCalendarView from './RetailSpaceCalendarView';
import RetailApplicationsView from './RetailApplicationsView';
import RetailApplicationsProcessedView from './RetailApplicationsProcessedView';
import * as RetailProfileActions from '../../../../../core/retail-profiles/action';
import * as VendorRetailActions from '../../../../../core/vendor-retail/action';
import './ManageRetailSpaces.scss';

function ManageRetailSpaces() {
  const dispatch = useDispatch();
  const { currentVenue } = useSelector((store) => store.venues);
  const { profiles, loading } = useSelector((store) => store.retailProfiles);
  const { applications, loading: applicationsLoading } = useSelector((store) => store.vendorRetail);

  // Load view mode from localStorage, default to 'grid' if not set
  const [viewMode, setViewMode] = useState(() => {
    const savedViewMode = localStorage.getItem('venueManageRetailSpacesViewMode');
    return savedViewMode || 'grid';
  });

  useEffect(() => {
    if (currentVenue?.id) {
      dispatch(RetailProfileActions.getRetailProfilesByVenue(currentVenue.id));
      dispatch(VendorRetailActions.getVenueRetailApplications(currentVenue.id));
    }
  }, [dispatch, currentVenue]);

  const retailSpaces = profiles || [];

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      localStorage.setItem('venueManageRetailSpacesViewMode', newMode);
    }
  };

  const handleDelete = (profileId) => {
    if (window.confirm('Are you sure you want to delete this retail profile?')) {
      dispatch(RetailProfileActions.deleteRetailProfile(profileId));
    }
  };

  const handleApplicationStatusChanged = (updatedApplication) => {
    // Refresh the applications list after status change
    if (currentVenue?.id) {
      dispatch(VendorRetailActions.getVenueRetailApplications(currentVenue.id));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'draft':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Render Grid View
  const renderGridView = () => {
    if (loading) {
      return (
        <Grid container marginTop={5} justifyContent="center">
          <TranslucentContainer>
            <Box textAlign="center" py={8}>
              <Typography variant="h5" color="text.secondary">
                Loading retail profiles...
              </Typography>
            </Box>
          </TranslucentContainer>
        </Grid>
      );
    }

    if (retailSpaces.length === 0) {
      return (
        <Grid container marginTop={5} justifyContent="center">
          <TranslucentContainer>
            <Grid container justifyContent="center" marginTop={8} minHeight="400px">
              <Grid container justifyContent="center" alignItems="center">
                <Box textAlign="center" py={8}>
                  <Typography variant="h5" color="text.secondary" gutterBottom>
                    No Retail Spaces Created Yet
                  </Typography>
                  <Typography variant="body1" color="text.secondary" marginTop={2}>
                    Create your first retail space using the &quot;Create Retail Profile&quot; tab
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </TranslucentContainer>
        </Grid>
      );
    }

    return (
      <Grid container marginTop={5} spacing={3} justifyContent="center">
        {retailSpaces.map((profile) => (
          <Grid item xs={12} sm={6} md={4} key={profile.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                    mb: 2,
                  }}>
                  <Typography variant="h6" fontWeight="bold" color="#333">
                    Retail Profile #{profile.id}
                  </Typography>
                  <Chip
                    label={profile.status}
                    color={getStatusColor(profile.status)}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" marginBottom={2}>
                  Venue: {profile.venue?.name || 'N/A'}
                </Typography>

                <Typography variant="body2" color="text.secondary" marginBottom={1}>
                  Schedules: {profile.schedules?.length || 0} day(s) configured
                </Typography>

                {(profile.start_date || profile.end_date) && (
                  <Typography variant="body2" color="text.secondary" marginBottom={2}>
                    Duration: {profile.start_date || 'No start'} - {profile.end_date || 'No end'}
                  </Typography>
                )}

                {profile.schedules && profile.schedules.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2 }}>
                    {profile.schedules.slice(0, 3).map((schedule) => (
                      <Chip
                        key={schedule.id}
                        label={schedule.day_of_week}
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    ))}
                    {profile.schedules.length > 3 && (
                      <Chip
                        label={`+${profile.schedules.length - 3}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(profile.id)}
                    sx={{ color: '#d32f2f' }}>
                    <Delete />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <div className="manage-retail-spaces">
      {/* View Toggle */}
      <Grid container justifyContent="center" marginTop={3}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          aria-label="view mode"
          sx={{
            backgroundColor: 'white',
            '& .MuiToggleButton-root': {
              border: '2px solid #EE7B57',
              color: '#EE7B57',
              fontWeight: 'bold',
              px: 3,
              '&.Mui-selected': {
                backgroundColor: '#EE7B57',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#D66A47',
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(238, 123, 87, 0.1)',
              },
            },
          }}>
          <ToggleButton value="grid" aria-label="grid view">
            <ViewModule sx={{ mr: 1 }} />
            Grid View
          </ToggleButton>
          <ToggleButton value="calendar" aria-label="calendar view">
            <CalendarMonth sx={{ mr: 1 }} />
            Calendar View
          </ToggleButton>
          <ToggleButton value="applications" aria-label="applications view">
            <Assignment sx={{ mr: 1 }} />
            Pending Applications
          </ToggleButton>
          <ToggleButton value="applications_completed" aria-label="completed applications view">
            <Assignment sx={{ mr: 1 }} />
            Completed Applications
          </ToggleButton>
        </ToggleButtonGroup>
      </Grid>

      {/* View Content */}
      {viewMode === 'grid' && renderGridView()}
      {viewMode === 'calendar' && <RetailSpaceCalendarView retailSpaces={retailSpaces} />}
      {viewMode === 'applications' && (
        <RetailApplicationsView
          applications={applications}
          onStatusChanged={handleApplicationStatusChanged}
          loading={applicationsLoading}
        />
      )}
      {viewMode === 'applications_completed' && (
        <RetailApplicationsProcessedView
          applications={applications}
          onStatusChanged={handleApplicationStatusChanged}
          loading={applicationsLoading}
        />
      )}
    </div>
  );
}

export default ManageRetailSpaces;
